// Handing it over, and telling the customer.
//
// Two artifacts and they are not the same kind of thing, which is why
// they are not next to each other on this screen without a line between
// them.
//
// The **job** is ciphertext (§20.9). It is a publish capability with the
// customer's phone number in it, so it never exists in the clear: what
// the QR carries and what the file holds is the sealed PME1 envelope,
// and a photograph of it is ciphertext. It opens on one phone.
//
// The **tracking link** is 78 bytes the customer is meant to have. It is
// read-only, it expires, and it says nothing about any other stop on the
// round.
//
// A **route day** (§21.11) goes through here unchanged, which is the
// whole point of it being a ticket: same QR, same file, same seal. Two
// sentences differ, and only because the facts do — the link is the
// term's rather than this run's, and what expires tonight is the
// authority inside the bytes rather than the link made from them.

import { recipientUrl, type FollowHandle, type FollowSnapshot } from "@wayfind/core";
import { Copyable, Note, Panel, Qr, downloadFile } from "./Bits";
import { DeliveryProgress } from "./Progress";
import { clockWords, plural, type Vocabulary } from "../ops/words";
import type { Dispatcher } from "../ops/dispatch";
import type { FleetState, StoredJob } from "../ops/state";

export function Handover({
  job,
  vocabulary,
  sealedToName,
  live,
  proof,
  state,
  dispatcher,
  onDone
}: {
  job: StoredJob;
  vocabulary: Vocabulary;
  /** Whose phone it opens on, in words. Null when only this console holds it. */
  sealedToName: string | null;
  /** What the run has said, when this console is following it. */
  live: FollowSnapshot | null;
  /** §20.7 — how a doorstep photo is fetched, when there is a follow. */
  proof: FollowHandle["proof"] | null;
  state: FleetState;
  dispatcher: Dispatcher | null;
  onDone(): void;
}): JSX.Element {
  const routeDay = job.serviceDay != null;
  return (
    <div className="stack">
      <Panel
        eyebrow={routeDay ? `Sent — service day ${job.serviceDay}` : "Sent"}
        aside={
          <button type="button" className="button button--small" onClick={onDone}>
            Send another
          </button>
        }
      >
        <h2 className="title">
          {job.stops.length === 1
            ? job.stops[0]!.label
            : `${plural(job.stops.length, vocabulary.stop)}, in order`}
        </h2>
        <p className="subtitle">
          Sealed at {clockWords(job.createdUnixSeconds)}, good until {clockWords(job.notAfter)}.{" "}
          {!job.heldBy
            ? "Nobody is holding it yet — it is sealed to this console until someone is free."
            : sealedToName
              ? `It opens on ${sealedToName} and on this console, and nowhere else.`
              : "It opens on this console, and nowhere else — pair a phone and it can go there too."}
        </p>
      </Panel>

      {/*
        Above the links, deliberately. Once a job is out, the question
        this screen answers stops being "how do I hand it over?" and
        becomes "did it arrive?" — and the operator coming back to this
        screen an hour later is asking the second one.
      */}
      <DeliveryProgress
        job={job}
        snapshot={live}
        vocabulary={vocabulary}
        state={state}
        dispatcher={dispatcher}
        proof={proof}
      />

      <Panel eyebrow={job.stops.length > 1
        ? `Give each ${vocabulary.recipient} their own`
        : `Give this to the ${vocabulary.recipient}`}>
        {/*
          One link per person, and they are not interchangeable.

          The capability is identical in all of them — the same 78 bytes,
          the same run. What differs is fifteen characters after it
          carrying **that person's own stop**, which is the only reason
          their page can say "not before 21:34" instead of "not yet
          known": the arrival is worked out on their device, and a device
          cannot route to a door it was never told about. A plan never
          goes on the wire (§20.8), so this is the one place the fact can
          be handed over, and it is handed to exactly one person.

          Sending everyone the same bare link still works and still
          tracks the van. It just cannot tell anybody when it reaches them.
        */}
        {job.stops.map((stop, index) => (
          <Copyable
            key={stop.id}
            label={job.stops.length > 1 ? `${index + 1}. ${stop.label}` : "Tracking link"}
            value={recipientUrl(job.trackUrl, { stopIndex: index + 1, lat: stop.lat, lon: stop.lon })}
            href={recipientUrl(job.trackUrl, { stopIndex: index + 1, lat: stop.lat, lon: stop.lon })}
            hint={index > 0 ? undefined : (
              routeDay
                ? "The capability is the same 78 bytes as yesterday — it is the "
                  + `${vocabulary.route}'s own link, good for the term — and it sits in the part of `
                  + "the address browsers never send anywhere. Whoever already has it needs nothing "
                  + "today. What follows it is this stop, so the page can work out an arrival."
                : "78 bytes in the part of the address browsers never send anywhere. No account, no "
                  + "install. Whoever holds one can watch this run until it expires, and learns "
                  + (job.stops.length > 1
                    ? "nothing about any other stop on it — each of these carries only its own."
                    : "nothing about any other stop on it.")
            )}
          />
        ))}
      </Panel>

      <hr className="divider" />

      <Panel eyebrow={`Give this to the ${vocabulary.driver}`}>
        <div className="handover handover--wide">
          <div>
            <Qr value={job.driverUrl} alt="The sealed job, as a code for a phone camera" />
            <p className="field__hint" style={{ marginTop: "0.45rem" }}>
              Point the phone at this. What the camera reads is ciphertext.
            </p>
          </div>
          <div className="stack stack--tight">
            <Copyable
              label={routeDay ? "Today’s run, sealed" : "The job, sealed"}
              value={job.driverUrl}
              hint={
                routeDay
                  ? "It carries the addresses and one day of authority over this "
                    + `${vocabulary.route} — the certificate saying so is inside these bytes and `
                    + "signed with them, so there is no second thing to send. Lost tonight, it is "
                    + "worth one day and never the term."
                  : "It carries the addresses, the instructions and the right to move the vehicle, "
                    + "so it is encrypted at all times — including while it is being handed over. A "
                    + "photo of the code on a counter is worth nothing to anyone else in the room."
              }
            />
            <div className="row">
              <button
                type="button"
                className="button"
                onClick={() => downloadFile(
                  `${job.jobIdHex.slice(0, 8)}.wayfindjob`,
                  job.driverUrl,
                  "text/plain"
                )}
              >
                Download the file
              </button>
              <span className="small muted">
                The identical sealed bytes. This is the carrier once a round outgrows a camera.
              </span>
            </div>
            <Note title="It only opens on the phone it was sealed to." tone="quiet">
              A phone that has not been paired gets told exactly that, and what to do about it. That
              is the trade: not “whoever holds these bytes”, but one phone you named.
            </Note>
          </div>
        </div>
      </Panel>
    </div>
  );
}
